import React, { useEffect, useState } from 'react'

// Read/edit card for a single party. Mirrors Scale's ClientInformation.
// Switches between read view (gray-50) and edit form (white).
export default function ClientInformation({
    client,
    label,
    onClientChange,
    isCompact = false,
}) {
    const [editing, setEditing] = useState(false)
    const buildDraft = (c) => ({
        name: c?.name || '',
        email: c?.email || '',
        phone: c?.phone || c?.phone_number || '',
        address: c?.address || '',
        invoice_emails: Array.isArray(c?.invoice_emails)
            ? c.invoice_emails
            : (c?.invoice_emails ? String(c.invoice_emails).split(',').map((s) => s.trim()).filter(Boolean) : []),
    })
    const [draft, setDraft] = useState(() => buildDraft(client))
    const [ccDraft, setCcDraft] = useState('')

    // When the parent renders with a different `client` (e.g. user switches
    // between plaintiff/defendant in step 1), reset draft to the new client.
    // Skip while editing to preserve in-flight typing.
    useEffect(() => {
        if (!editing) setDraft(buildDraft(client))
    // eslint-disable-next-line
    }, [client?.client_id, editing])

    if (!client) return null

    const handleSave = () => {
        onClientChange?.(client.client_id, {
            client_information: {
                name: draft.name,
                email: draft.email,
                phone_number: draft.phone,
                address: draft.address,
            },
            invoice_emails: draft.invoice_emails.join(', '),
        })
        setEditing(false)
    }

    const handleCancel = () => {
        setDraft({
            name: client?.name || '',
            email: client?.email || '',
            phone: client?.phone || client?.phone_number || '',
            address: client?.address || '',
            invoice_emails: Array.isArray(client?.invoice_emails)
                ? client.invoice_emails
                : (client?.invoice_emails ? String(client.invoice_emails).split(',').map((s) => s.trim()).filter(Boolean) : []),
        })
        setEditing(false)
    }

    const addCc = () => {
        const v = ccDraft.trim()
        if (!v) return
        if (v === draft.email) return
        if (draft.invoice_emails.includes(v)) return
        setDraft((p) => ({ ...p, invoice_emails: [...p.invoice_emails, v] }))
        setCcDraft('')
    }

    // Header reads "Plaintiff (Display Name)" or "Defendant (Display Name)" — Scale parity.
    const headerLabel = (() => {
        const role = label || (client.role ? client.role.charAt(0).toUpperCase() + client.role.slice(1) : 'Party')
        const name = client.name || ''
        return name ? `${role} (${name})` : role
    })()

    return (
        <div className={`rounded-lg border border-gray-200 overflow-hidden ${editing ? 'bg-white' : 'bg-white'}`}>
            <div className="flex items-center justify-between px-4 py-2.5 bg-[#F9FAFB] border-b border-gray-100">
                <div className="text-sm font-semibold text-gray-900">{headerLabel}</div>
                {!editing ? (
                    <button type="button" onClick={() => setEditing(true)} className="text-gray-400 hover:text-teal-700" title="Edit">
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path d="M14.69 2.66l2.65 2.65-9.5 9.5H5.19v-2.65l9.5-9.5zm3.18-1.06a1.875 1.875 0 010 2.65l-1.06 1.06-2.65-2.65 1.06-1.06a1.875 1.875 0 012.65 0z"/></svg>
                    </button>
                ) : null}
            </div>

            {!editing ? (
                <div className="px-4 py-3 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-gray-500">Name:</span>
                        <span className="text-gray-900">{client.name || '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-gray-500">Email:</span>
                        <span className="text-gray-900">{client.email || '—'}</span>
                    </div>
                    {(client.phone || client.phone_number) ? (
                        <div className="flex items-center justify-between">
                            <span className="text-gray-500">Phone:</span>
                            <span className="text-gray-900">{client.phone || client.phone_number}</span>
                        </div>
                    ) : null}
                    {client.address ? (
                        <div className="flex items-center justify-between">
                            <span className="text-gray-500">Address:</span>
                            <span className="text-gray-900">{client.address}</span>
                        </div>
                    ) : null}
                    {Array.isArray(client.invoice_emails) && client.invoice_emails.length ? (
                        <div className="flex items-start justify-between">
                            <span className="text-gray-500">CC:</span>
                            <span className="text-gray-900 text-right">{client.invoice_emails.join(', ')}</span>
                        </div>
                    ) : null}
                </div>
            ) : (
                <div className="px-4 py-3">
                <div className="space-y-2">
                    <input className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" placeholder="Name" value={draft.name} onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))} />
                    <input className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" placeholder="Email" value={draft.email} onChange={(e) => setDraft((p) => ({ ...p, email: e.target.value }))} />
                    <input className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" placeholder="Phone" value={draft.phone} onChange={(e) => setDraft((p) => ({ ...p, phone: e.target.value }))} />
                    <input className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" placeholder="Address" value={draft.address} onChange={(e) => setDraft((p) => ({ ...p, address: e.target.value }))} />
                    <div>
                        <div className="text-xs text-gray-600 mb-1">CC emails</div>
                        <div className="flex flex-wrap gap-1 mb-1">
                            {draft.invoice_emails.map((em) => (
                                <span key={em} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-xs rounded">
                                    {em}
                                    <button type="button" onClick={() => setDraft((p) => ({ ...p, invoice_emails: p.invoice_emails.filter((x) => x !== em) }))} className="text-gray-500 hover:text-red-500">×</button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input className="flex-1 text-sm border border-gray-300 rounded px-2 py-1.5" placeholder="Add CC email" value={ccDraft} onChange={(e) => setCcDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCc() } }} />
                            <button type="button" onClick={addCc} className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50">Add</button>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={handleCancel} className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                        <button type="button" onClick={handleSave} className="text-xs px-3 py-1.5 text-white rounded" style={{ backgroundColor: '#115E59' }}>Save</button>
                    </div>
                </div>
                </div>
            )}
        </div>
    )
}
